// Copy, cut, paste, duplicate, nudge, mute — the decisions, without React.
//
// Three rules shape this file.
//
// 1. A SELECTION IS A SET OF IDS, AND IDS GO STALE. The room never resurrects a
//    clip: undoing a delete recreates it under a new id (lib/undo.ts), and a
//    refused create never had one. So every operation starts by resolving the
//    ids against the room it is about to edit, and a count shown to a person is
//    a count of clips that exist.
//
// 2. A SELECTION MOVES RIGIDLY. Whatever the gesture — a nudge, a paste, a
//    duplicate — the clips keep their spacing in time AND their spacing across
//    lanes. Anything that resolves each member independently turns a vertical
//    stack into a horizontal run, or shears a run in time, silently.
//
// 3. WHAT THE ROOM WOULD REFUSE IS DECIDED HERE. Overlap and kind mismatch are
//    the two rejections a browser can predict exactly (placement.ts's header
//    makes the case), so a gesture either proposes something legal or says why
//    it did nothing. Nothing here proposes; the hook above it does.

import type {
  CanonicalClipState,
  CanonicalRoom,
  CanonicalTrackState,
} from "@shared/sync/state.js";
import { findClip } from "@web-shared/lib/edits";
import { collidesExcept } from "@web-shared/lib/placement";

/** A clip's kind, with the default the wire leaves implicit. */
function kindOf(clip: CanonicalClipState): "audio" | "midi" {
  return (clip.clipKind ?? "audio") === "midi" ? "midi" : "audio";
}

/** A lane's kind, likewise. */
function laneKind(track: CanonicalTrackState): "audio" | "midi" {
  return (track.trackKind ?? "audio") === "midi" ? "midi" : "audio";
}

/** The Set's lanes in the order it displays them — the order ↑/↓ and a paste's
 *  lane offsets are measured in (`moveClipsToLane` uses the same). */
export function orderedLanes(room: CanonicalRoom): CanonicalTrackState[] {
  return [...room.tracks].sort((a, b) => a.ord - b.ord);
}

/**
 * The selected ids as clips the timeline's operations can act on: the ones the
 * room still holds, and not the Session View's, which have no position on the
 * timeline to move, copy or nudge.
 *
 * Everything downstream counts THIS, never `selected.size` — otherwise the bar
 * says "3 clips selected" after an undo has replaced all three.
 */
export function liveSelection(room: CanonicalRoom | null, ids: Iterable<string>): CanonicalClipState[] {
  if (!room) return [];
  const clips: CanonicalClipState[] = [];
  for (const id of ids) {
    const clip = findClip(room, id);
    if (clip !== null && clip.slot === undefined) clips.push(clip);
  }
  return clips;
}

/**
 * The clip a gesture is measured from: the topmost lane's, earliest first.
 *
 * Deliberately NOT "the first one in the Set" — a Set is in insertion order,
 * which for a rubber band is whichever clip the hit test happened to reach
 * first. Two identical selections would then paste to two different lanes.
 */
export function anchorClip(room: CanonicalRoom, clips: CanonicalClipState[]): CanonicalClipState | null {
  const ord = new Map(room.tracks.map((t) => [t.id, t.ord]));
  let best: CanonicalClipState | null = null;
  let bestOrd = Infinity;
  for (const clip of clips) {
    const o = ord.get(clip.trackId) ?? Infinity;
    if (best === null || o < bestOrd || (o === bestOrd && clip.startTime < best.startTime)) {
      best = clip;
      bestOrd = o;
    }
  }
  return best;
}

/**
 * Which lane a paste should aim at. Deterministic, in this order:
 *
 * 1. The selection's anchor lane — the producer pointed at a clip, so the paste
 *    goes where they are looking.
 * 2. The lane a thumb last opened the mixer strip on. A weak signal (it toggles
 *    off on a second tap), which is why it ranks below a selection — but on a
 *    phone it is often the only thing said about "where".
 * 3. The anchor source's own lane: back where it came from.
 * 4. The first lane that could hold it at all.
 *
 * At every step the lane's kind must match the material's: the room refuses a
 * MIDI clip on an audio lane and the reverse, so a lane that cannot take it is
 * not a candidate. Null ⇒ tell the producer, don't propose.
 */
export function pasteTargetLane(
  room: CanonicalRoom,
  selection: CanonicalClipState[],
  fallbackLaneId: string | null,
  sources: CanonicalClipState[],
): CanonicalTrackState | null {
  const anchorSource = anchorClip(room, sources);
  if (!anchorSource) return null;
  const wanted = kindOf(anchorSource);
  const fits = (track: CanonicalTrackState | undefined): CanonicalTrackState | null =>
    track && laneKind(track) === wanted ? track : null;
  const byId = (id: string | null): CanonicalTrackState | null =>
    id === null ? null : fits(room.tracks.find((t) => t.id === id));

  const anchorSelected = anchorClip(room, selection);
  return (
    byId(anchorSelected?.trackId ?? null) ??
    byId(fallbackLaneId) ??
    byId(anchorSource.trackId) ??
    orderedLanes(room).find((t) => laneKind(t) === wanted) ??
    null
  );
}

/**
 * Where each source lands, lane-wise, once the block is aimed at `targetLaneId`:
 * its own distance from the anchor source, carried over.
 *
 * A source whose offset runs off the end of the Set, or lands on a lane of the
 * wrong kind, gets null — skipped and counted. It is NOT folded onto the target
 * lane: a two-lane copy that came back stacked on one lane would be a silent
 * lie about what was pasted.
 */
export function pasteLaneMap(
  room: CanonicalRoom,
  sources: CanonicalClipState[],
  targetLaneId: string,
): (clip: CanonicalClipState) => string | null {
  const lanes = orderedLanes(room);
  const indexOf = new Map(lanes.map((t, i) => [t.id, i]));
  const anchor = anchorClip(room, sources);
  const anchorIndex = anchor ? indexOf.get(anchor.trackId) : undefined;
  const targetIndex = indexOf.get(targetLaneId);

  return (clip) => {
    if (targetIndex === undefined) return null;
    const from = indexOf.get(clip.trackId);
    if (from === undefined || anchorIndex === undefined) return null;
    const lane = lanes[targetIndex + (from - anchorIndex)];
    if (!lane || laneKind(lane) !== kindOf(clip)) return null;
    return lane.id;
  };
}

/** Where a duplicate of this selection begins: right after all of it. */
export function duplicateAt(clips: CanonicalClipState[]): number {
  return clips.reduce((end, c) => Math.max(end, c.startTime + c.duration), 0);
}

/**
 * How far the selection can actually travel when asked to move `delta`.
 *
 * Clamped as a BLOCK, not per clip. `moveClips` floors each member at 0 on its
 * own, so a run nudged left off the start of the Set folds onto itself and the
 * room accepts some members and refuses others — a half-moved selection nobody
 * asked for. The block stops when its earliest member reaches 0.
 */
export function nudgeDelta(clips: CanonicalClipState[], delta: number): number {
  if (clips.length === 0 || delta >= 0) return delta;
  const earliest = clips.reduce((min, c) => Math.min(min, c.startTime), Infinity);
  return Math.max(delta, -earliest);
}

/**
 * Whether something that isn't moving is in the way.
 *
 * The selection is exempt from its own test: a contiguous run sliding one beat
 * along is forever overlapping its next member, and that member is travelling
 * too.
 */
export function nudgeBlocked(room: CanonicalRoom, clips: CanonicalClipState[], delta: number): boolean {
  const moving = new Set(clips.map((c) => c.id));
  return clips.some((c) => collidesExcept(room, c.trackId, c.startTime + delta, c.duration, moving));
}

/**
 * One muted value for the whole selection: mute unless everything already is.
 *
 * Flipping each clip's own flag looks the same on a single clip and is wrong on
 * a mixed selection — it INVERTS it, muting what was audible and unmuting what
 * was not, which is never what pressing "mute" meant.
 */
export function muteTarget(clips: CanonicalClipState[]): boolean {
  return clips.some((c) => !c.muted);
}

// ---------------------------------------------------------------------------
// What to say about it
// ---------------------------------------------------------------------------
//
// A phone has no menu to check and no console to read, so an operation that
// declined to do something has to say so in words. These are pure so the
// wording is testable and lives next to the rule it describes.

function clipCount(n: number): string {
  return `${n} clip${n === 1 ? "" : "s"}`;
}

/** Nothing on the clipboard could be copied — every source is a clip with no
 *  object and no notes. */
export const NOTHING_TO_COPY = "These clips have nothing to copy.";
export const NOTHING_TO_PASTE = "Nothing to paste yet.";
export const NO_LANE_TO_PASTE_ONTO = "No lane of that kind to paste onto.";
export const SOMETHING_IN_THE_WAY = "A clip is in the way.";

export function copiedNote(copied: number, selected: number): string {
  const head = `Copied ${clipCount(copied)}.`;
  const short = selected - copied;
  return short > 0 ? `${head} ${clipCount(short)} had nothing to copy.` : head;
}

/** A span's length as a person counts it: "1 beat", "0.25 beats". */
function beatCount(length: number): string {
  const n = Math.round(length * 1000) / 1000;
  return `${n} beat${n === 1 ? "" : "s"}`;
}

export function copiedSpanNote(length: number, clips: number, skipped: number): string {
  const head = clips > 0 ? `Copied ${beatCount(length)} (${clipCount(clips)}).` : `Copied ${beatCount(length)} of silence.`;
  return skipped > 0 ? `${head} ${clipCount(skipped)} had nothing to copy.` : head;
}

export function cutSpanNote(length: number): string {
  return `Cut ${beatCount(length)}.`;
}

export const SPAN_UNCUTTABLE =
  "A clip in the selection can't be cut there: a transposed unwarped clip only cuts once it's warped.";

export function deletedNote(deleted: number): string {
  return `Deleted ${clipCount(deleted)} — undo is in the bar below.`;
}

export function cutNote(deleted: number, carried: number): string {
  const head = `Cut ${clipCount(deleted)}.`;
  const short = deleted - carried;
  return short > 0 ? `${head} ${clipCount(short)} could not be carried.` : head;
}

export function pastedNote(landed: number, skipped: number): string {
  if (skipped === 0) return `Pasted ${clipCount(landed)}.`;
  return `Pasted ${landed} — ${skipped} had no lane to land on.`;
}

/** A lane with no room left inside the search horizon. 1024 beats of hunting is
 *  256 bars in four-four; saying "full" is truer than saying "failed". */
export function blockedNote(laneName: string | null): string {
  return laneName === null
    ? "No room for that — the next 256 bars are full."
    : `No room for that on ${laneName} — the next 256 bars are full.`;
}

export function noLaneNote(direction: -1 | 1): string {
  const where = direction === -1 ? "above" : "below";
  return `No lane ${where} of that kind, or it's already occupied there.`;
}
